import { useQuery } from "@tanstack/react-query";

export type GuestReview = {
  id: string | number;
  source: string;
  authorName: string;
  rating: number;
  text: string;
  relativeTime?: string | null;
  date?: string | null;
  profilePhotoUrl: string | null;
  permalink: string | null;
};

export type GuestReviewsResponse = {
  reviews: GuestReview[];
  rating: number | null;
  totalReviews: number | null;
  reviewsUrl: string;
  writeReviewUrl: string;
  source?: { kind: string; capturedAt?: string; origin?: string };
  aggregateSource?: { kind: string; capturedOn?: string };
};

// The shared query function throws errors prefixed with the HTTP status.
// Fetch failures have no HTTP response; neither case should retry client errors.
function isTransientReviewError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const status = /^(\d{3}):/.exec(error.message);
  if (status) return Number(status[1]) >= 500 && Number(status[1]) < 600;
  return error instanceof TypeError || /Failed to fetch|NetworkError|Load failed|network request failed/i.test(error.message);
}

export function useGuestReviews() {
  return useQuery<GuestReviewsResponse>({
    queryKey: ["/api/guest-reviews"],
    retry: (failureCount, error) => failureCount < 3 && isTransientReviewError(error),
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 4000),
    // Successful reviews use the shared stale-time policy, without polling.
    // A failed refresh retains the previous data while recovery is attempted.
    refetchOnWindowFocus: query => !!query.state.error && isTransientReviewError(query.state.error),
    refetchOnReconnect: query => !!query.state.error && isTransientReviewError(query.state.error),
    refetchInterval: query => query.state.error && isTransientReviewError(query.state.error) ? 30_000 : false,
  });
}